// Layer 3 (Transformation): pure projections from Reading tuples — no React,
// no engine mutations. See docs/schema/reading.md § Derivations.

import type {
  AlarmSetpoints,
  Band,
  Reading,
  ReadingScope,
  ReadingStatus,
  ReadingTone,
} from '@/versions/v1/schema/reading';
import type { ProcessConstraint } from '@/versions/v1/schema/unit-operation';
import type { StreamSpec } from '@/versions/v1/schema/stream';
import type { SessionState } from '@/versions/v1/schema/scenario';
import {
  CONSTRAINT_COPY,
  CONSTRAINT_DISPLAY_OVERRIDE,
  READINGS,
  STREAM_SPEC_COPY,
  UNIT_KEY_PARAM_COPY,
} from './bunker-hill-readings';
import { TAG_MAPPING } from './bunker-hill-spc';
import { excursions, valueBandPosition, valueConformance } from './SpcEngine';

const readingById = new Map(READINGS.map((row) => [row.id, row]));

export function readingByIdLookup(id: string): Reading | undefined {
  return readingById.get(id);
}

function scopeKey(scope: ReadingScope): string {
  if (scope.kind === 'plant') return 'plant';
  return `${scope.kind}:${scope.id}`;
}

const readingsByScope = new Map<string, Reading[]>();
for (const row of READINGS) {
  const key = scopeKey(row.scope);
  const bucket = readingsByScope.get(key) ?? [];
  bucket.push(row);
  readingsByScope.set(key, bucket);
}

const TONE_RANK: Record<ReadingTone, number> = {
  bad: 3,
  warn: 2,
  ok: 1,
  neutral: 0,
};

function worstTone(tones: readonly ReadingTone[]): ReadingTone {
  let worst: ReadingTone = 'neutral';
  let rank = -1;
  for (const tone of tones) {
    const score = TONE_RANK[tone];
    if (score > rank) {
      rank = score;
      worst = tone;
    }
  }
  return worst;
}

/**
 * Decimal places implied by a raw number's own string form (e.g. 12.13 → 2).
 * Cannot recover a trailing zero a source intended (58.0 → "58" → 0) — that
 * case is what `Reading.decimals` exists to carry. Exported so call sites
 * that format a *second* number through a reading (e.g. its `design` point,
 * which can carry its own, different natural precision than the live
 * value `reading.decimals` is sized for) can combine the two rather than
 * let one silently truncate the other (`pl.D1` #142 round 4).
 */
export function precisionFromNumber(n: number): number {
  const s = String(n);
  const dot = s.indexOf('.');
  if (dot === -1) return 0;
  return s.length - dot - 1;
}

function crossedAlarm(
  value: number,
  alarms: AlarmSetpoints | undefined,
): keyof AlarmSetpoints | undefined {
  if (!alarms) return undefined;
  const order: (keyof AlarmSetpoints)[] = ['HH', 'H', 'L', 'LL'];
  for (const key of order) {
    const threshold = alarms[key];
    if (threshold === undefined) continue;
    if ((key === 'HH' || key === 'H') && value >= threshold) return key;
    if ((key === 'LL' || key === 'L') && value <= threshold) return key;
  }
  return undefined;
}

function bandPosition(
  value: number | undefined,
  band: Band | undefined,
): ReadingStatus['bandPosition'] {
  return valueBandPosition(value, band);
}

function conformance(
  value: number | undefined,
  spec: Band | undefined,
): ReadingStatus['conformance'] {
  if (value === undefined || !spec) return 'n/a';
  const result = valueConformance(value, spec);
  return result === false ? 'non-conforming' : 'conforming';
}

export type FormatValueOpts = {
  withUnit?: boolean;
  decimals?: number;
  value?: number;
};

export type FormatBandOpts = {
  withUnit?: boolean;
  decimals?: number;
  spacedDash?: boolean;
};

function unitSuffix(unit: string, withUnit: boolean): string {
  if (!withUnit || !unit) return '';
  if (unit.startsWith('%') || unit.endsWith('%')) return unit;
  return ` ${unit}`;
}

export function deviationTone(
  pct: number,
  higherIsBetter?: boolean,
): ReadingTone {
  if (higherIsBetter === undefined) return 'neutral';
  const effective = higherIsBetter ? pct : -pct;
  if (effective >= -0.5) return 'ok';
  if (effective >= -1.5) return 'warn';
  return 'bad';
}

export function formatValue(reading: Reading, opts?: FormatValueOpts): string {
  const withUnit = opts?.withUnit ?? false;
  const num = opts?.value ?? reading.value ?? reading.design ?? reading.target;
  if (num === undefined) return '—';
  const n = opts?.decimals ?? reading.decimals ?? precisionFromNumber(num);
  const formatted = num.toLocaleString('en-US', {
    minimumFractionDigits: n,
    maximumFractionDigits: n,
  });
  return formatted + unitSuffix(reading.unit, withUnit);
}

export function formatBand(
  band: Band,
  unit: string,
  opts?: FormatBandOpts,
): string {
  const withUnit = opts?.withUnit ?? Boolean(unit);
  const decimals = opts?.decimals;
  const dash = opts?.spacedDash ? ' – ' : '–';
  const fmt = (n: number) => {
    const d = decimals ?? precisionFromNumber(n);
    return n.toLocaleString('en-US', {
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    });
  };
  const u = unitSuffix(unit, withUnit);
  if (band.lo !== undefined && band.hi !== undefined) {
    return `${fmt(band.lo)}${dash}${fmt(band.hi)}${u}`;
  }
  if (band.hi !== undefined) {
    return `< ${fmt(band.hi)}${u}`;
  }
  if (band.lo !== undefined) {
    return `≥ ${fmt(band.lo)}${u}`;
  }
  return '—';
}

function formatReadingSegment(
  reading: Reading,
  segment: { prefix?: string; withUnit?: boolean },
): string {
  const withUnit = segment.withUnit ?? false;
  const prefix = segment.prefix ?? '';
  return `${prefix}${formatValue(reading, { withUnit })}`;
}

export function formatReading(reading: Reading): string {
  return formatValue(reading, { withUnit: false });
}

export function formatUnitKeyParam(
  unitId: string,
  ids: readonly string[],
  readings: readonly Reading[] = READINGS,
): string {
  const copy = UNIT_KEY_PARAM_COPY[unitId];
  if (copy?.verbatim) return copy.verbatim;
  const byId = new Map(readings.map((row) => [row.id, row]));
  if (copy?.segments) {
    return copy.segments
      .map((segment) => {
      if ('verbatim' in segment) return segment.verbatim;
      const row = byId.get(segment.id);
      if (!row) return '—';
      return formatReadingSegment(row, segment);
    })
      .join(' · ');
  }
  return ids
    .map((id) => byId.get(id))
    .filter((row): row is Reading => Boolean(row))
    .map((row) => formatReading(row))
    .join(' · ');
}

export function formatReadings(ids: readonly string[]): string {
  return formatReadingsFrom(ids, READINGS);
}

export function formatReadingsFrom(
  ids: readonly string[],
  readings: readonly Reading[],
): string {
  const byId = new Map(readings.map((row) => [row.id, row]));
  return ids
    .map((id) => byId.get(id))
    .filter((row): row is Reading => Boolean(row))
    .map((row) => formatReading(row))
    .join(' · ');
}

export function readingStatus(reading: Reading): ReadingStatus {
  const reference = reading.target ?? reading.design;
  const value = reading.value;
  const bandPos = bandPosition(value, reading.band);
  const conf = conformance(value, reading.spec);
  const alarm = value !== undefined ? crossedAlarm(value, reading.alarms) : undefined;

  let deviationPct: number | undefined;
  if (value !== undefined && reference !== undefined && reference !== 0) {
    deviationPct = ((value - reference) / reference) * 100;
  }

  if (
    !reading.band &&
    !reading.dcsRange &&
    !reading.spec &&
    !reading.alarms &&
    reference === undefined
  ) {
    return { tone: 'neutral', deviationPct, bandPosition: bandPos, conformance: conf, alarm };
  }

  if (alarm) {
    return { tone: 'bad', deviationPct, bandPosition: bandPos, conformance: conf, alarm };
  }

  if (value !== undefined && reading.dcsRange) {
    const { lo, hi } = reading.dcsRange;
    if ((lo !== undefined && value < lo) || (hi !== undefined && value > hi)) {
      return { tone: 'bad', deviationPct, bandPosition: bandPos, conformance: conf, alarm };
    }
  }

  if (bandPos === 'below' || bandPos === 'above') {
    return { tone: 'warn', deviationPct, bandPosition: bandPos, conformance: conf, alarm };
  }

  if (conf === 'non-conforming') {
    return { tone: 'warn', deviationPct, bandPosition: bandPos, conformance: conf, alarm };
  }

  if (deviationPct !== undefined && reference !== undefined) {
    const targetOnly =
      reading.target !== undefined &&
      reading.design === undefined &&
      reference === reading.target;
    if (!reading.band && targetOnly) {
      return {
        tone: 'ok',
        deviationPct,
        bandPosition: bandPos,
        conformance: conf,
        alarm,
      };
    }
    let tone = deviationTone(deviationPct, reading.higherIsBetter);
    if (!reading.band && tone === 'bad') {
      tone = 'warn';
    }
    return {
      tone,
      deviationPct,
      bandPosition: bandPos,
      conformance: conf,
      alarm,
    };
  }

  return { tone: 'ok', deviationPct, bandPosition: bandPos, conformance: conf, alarm };
}

export function healthForReadingIds(
  ids: readonly string[],
  readings: readonly Reading[] = READINGS,
  state?: SessionState,
): ReadingTone {
  const byId = new Map(readings.map((row) => [row.id, row]));
  const tones = ids
    .map((id) => byId.get(id))
    .filter((row): row is Reading => Boolean(row))
    .map((row) => readingStatus(row).tone);
  if (tones.length === 0) return 'neutral';
  let tone = worstTone(tones);
  if (!state) return tone;

  const subprocessIds = new Set<string>();
  for (const id of ids) {
    const row = byId.get(id);
    if (row?.scope.kind === 'subprocess') subprocessIds.add(row.scope.id);
  }
  for (const subprocessId of subprocessIds) {
    tone = worstTone([tone, scopeHealth({ kind: 'subprocess', id: subprocessId }, readings, state)]);
  }
  return tone;
}

export function utilisationPct(reading: Reading): number | undefined {
  if (reading.kind !== 'rate' || reading.value === undefined || reading.band?.hi === undefined) {
    return undefined;
  }
  return Math.round((reading.value / reading.band.hi) * 100);
}

export function readingsForScope(
  scope: ReadingScope,
  readings: readonly Reading[] = READINGS,
): Reading[] {
  if (readings === READINGS) {
    return readingsByScope.get(scopeKey(scope))?.slice() ?? [];
  }
  return readings.filter((row) => scopeKey(row.scope) === scopeKey(scope));
}

export function scopeHealth(
  scope: ReadingScope,
  readings: readonly Reading[] = READINGS,
  state?: SessionState,
): ReadingTone {
  const rows = readingsForScope(scope, readings);
  if (rows.length === 0) return 'neutral';
  let tone = worstTone(rows.map((row) => readingStatus(row).tone));
  if (!state || scope.kind !== 'subprocess') return tone;

  const subprocessId = scope.id;
  const rolesOnNode = new Set(
    TAG_MAPPING.filter((row) => row.subprocessId === subprocessId).map((row) => row.seriesId),
  );
  for (const event of excursions(state)) {
    const suffix = event.variableId.replace(/^mv-/, '');
    const seriesId = `rs-${suffix}`;
    if (!rolesOnNode.has(seriesId)) continue;
    if (event.severity === 'strong') return 'bad';
    if (event.severity === 'signal' && TONE_RANK[tone] < TONE_RANK.warn) tone = 'warn';
  }
  return tone;
}

function formatConstraintAlarms(alarms: AlarmSetpoints | undefined): string | undefined {
  if (!alarms) return undefined;
  const parts: string[] = [];
  if (alarms.LL !== undefined) parts.push(`LL ${alarms.LL}`);
  if (alarms.L !== undefined) parts.push(`L ${alarms.L}`);
  if (alarms.H !== undefined) parts.push(`H ${alarms.H}`);
  if (alarms.HH !== undefined) parts.push(`HH ${alarms.HH}`);
  if (parts.length === 0) return undefined;
  return parts.join('/');
}

export function constraintFromReading(reading: Reading): ProcessConstraint {
  const subprocessId =
    reading.scope.kind === 'subprocess' ? reading.scope.id : undefined;
  const copy = CONSTRAINT_COPY[reading.id];
  const override = CONSTRAINT_DISPLAY_OVERRIDE[reading.id];

  const dcsRange =
    override?.dcsRange ??
    copy?.dcsRange ??
    (reading.dcsRange
      ? formatBand(reading.dcsRange, reading.unit, { spacedDash: true, withUnit: true })
      : '—');

  let normalRange = override?.normalRange ?? copy?.normalRange;
  if (!normalRange) {
    if (reading.band) {
      normalRange = formatBand(reading.band, reading.unit, {
        spacedDash: true,
        withUnit: true,
        decimals: reading.decimals,
      });
      if (reading.design !== undefined) {
        normalRange = `${normalRange} (design ${formatValue(reading, {
          withUnit: true,
          value: reading.design,
        })})`;
      }
    } else if (reading.design !== undefined && !reading.band) {
      normalRange = `design ${formatValue(reading, { withUnit: true, value: reading.design })}`;
    } else if (reading.alarms?.H !== undefined && !reading.band) {
      normalRange = `High alarm ${reading.alarms.H}${unitSuffix(reading.unit, true)}`;
    } else {
      normalRange = '—';
    }
  }

  const alarms = copy?.alarms ?? formatConstraintAlarms(reading.alarms);

  return {
    id: reading.id.replace(/^rd-/, ''),
    point: reading.label,
    dcsRange,
    normalRange,
    alarms,
    subprocessId,
  };
}

export type StreamSpecCopy = {
  consequence: string;
  provenance?: string;
  target?: string;
};

export function specFromReading(
  reading: Reading,
  copy: StreamSpecCopy,
): StreamSpec {
  const streamId = reading.scope.kind === 'stream' ? reading.scope.id : '';
  const specCopy = STREAM_SPEC_COPY[reading.id];
  let target = copy.target ?? specCopy?.target;
  if (!target) {
    if (reading.spec !== undefined) {
      target = formatBand(reading.spec, reading.unit, {
        withUnit: true,
        decimals: reading.decimals,
      });
    } else if (reading.design !== undefined) {
      target = formatValue(reading, { withUnit: true, value: reading.design });
    } else if (reading.value !== undefined) {
      target = formatValue(reading, { withUnit: true });
    } else {
      target = '—';
    }
  }
  return {
    id: reading.id.replace(/^rd-/, ''),
    streamId,
    parameter: reading.label,
    target,
    consequence: copy.consequence,
    provenance:
      copy.provenance ?? reading.provenanceByField?.spec ?? reading.provenance,
  };
}
